import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar, PriorityFlag, StatusPill } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { ActivityFeed } from '@/features/activity/ActivityFeed'
import { useSession } from '@/features/auth/useSession'
import { fetchBoard } from '@/features/boards/api'
import { TaskExtras } from '@/features/work-items/TaskExtras'
import { fetchPeople, type Person } from '@/features/people/api'
import {
  createWorkItem,
  deleteWorkItem,
  fetchWorkItem,
  linkWorkItems,
  unlinkWorkItems,
  updateWorkItem,
  type WorkItemChanges,
} from '@/features/work-items/api'
import {
  deleteComment,
  fetchComments,
  fetchLinks,
  postComment,
  updateComment,
  type Comment,
  type ItemLinks,
} from '@/features/work-items/comments'
import type { ApiError } from '@/lib/api/client'
import type { Priority, Visibility, WorkItemType } from '@/types/domain'
import type { Board, WorkItem, WorkItemChild } from '@/types/work'

const PRIORITIES: Priority[] = ['urgent', 'high', 'normal', 'low']
const TYPES: WorkItemType[] = ['task', 'bug', 'feature', 'milestone', 'risk', 'decision']

/**
 * A work item, opened over the list rather than instead of it.
 *
 * A slide-over keeps the board or the list behind it, so reading one card does
 * not cost the place you were in — which is what makes triaging twenty of them
 * bearable.
 *
 * The four fields at the top are editable here because this is where someone
 * actually decides them. Reading who owns a task and having to go elsewhere to
 * change it is how a task stays unassigned.
 *
 * The composer's visibility control is deliberately loud. An internal note and
 * a note the client reads look nothing alike here, because this is the single
 * most likely place in the product for an accidental disclosure.
 *
 * Mount this with `key={item.id}` — the panel holds the server's last answer
 * for the item in local state, and a different item needs a fresh one.
 */
export function TaskPanel({
  item,
  projectKey,
  onClose,
}: {
  item: WorkItem
  projectKey?: string | undefined
  onClose: () => void
}) {
  const { t } = useTranslation()
  const session = useSession()
  const [tab, setTab] = useState<'comments' | 'activity'>('comments')

  const queryClient = useQueryClient()

  // The parent's copy is a row in a list that has not refetched yet, so the
  // server's answer to the last edit is the truer one.
  const save = useMutation<WorkItem, ApiError, WorkItemChanges>({
    mutationFn: (changes) => updateWorkItem(item.id, changes),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-item', item.id] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      void queryClient.invalidateQueries({ queryKey: ['board'] })
      void queryClient.invalidateQueries({ queryKey: ['timeline'] })
      // An edit is an activity row, so the feed behind this panel is stale.
      void queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteWorkItem(item.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      void queryClient.invalidateQueries({ queryKey: ['board'] })
      onClose()
    },
  })

  const comments = useQuery<Comment[], ApiError>({
    queryKey: ['comments', item.id],
    queryFn: () => fetchComments(item.id),
    retry: false,
  })

  const links = useQuery<ItemLinks, ApiError>({
    queryKey: ['links', item.id],
    queryFn: () => fetchLinks(item.id),
    retry: false,
  })

  // The row that opened this panel came from a list, and a list payload carries
  // no children and no parent. Reading the item once fills them in without
  // making the panel wait: everything else renders from the row meanwhile.
  const full = useQuery<WorkItem, ApiError>({
    queryKey: ['work-item', item.id],
    queryFn: () => fetchWorkItem(item.id),
    retry: false,
  })

  const shown = save.data ?? full.data ?? item
  const key = projectKey ?? shown.project?.key

  return (
    <>
      <button
        type="button"
        aria-label={t('view.close')}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/20"
      />

      <aside className="border-line bg-surface fixed inset-y-0 end-0 z-50 flex w-full max-w-xl flex-col border-s shadow-2xl">
        <header className="border-line flex items-start gap-3 border-b px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-brand-600 font-mono text-[11px]">{shown.key}</span>
              {shown.parent == null ? null : (
                <span className="text-ink-500 truncate font-mono text-[11px]">
                  {'↳ '}
                  {shown.parent.key}
                </span>
              )}
            </div>
            <InlineText
              value={shown.title}
              label={t('view.itemTitle')}
              disabled={save.isPending}
              onCommit={(value) => {
                if (value !== '' && value !== shown.title) save.mutate({ title: value })
              }}
              className="text-ink-900 mt-0.5 w-full rounded text-lg leading-snug font-semibold"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t('view.close')}
            className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1.5"
          >
            <Icon name="close" className="size-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <Fields
            item={shown}
            projectKey={key}
            saving={save.isPending}
            onChange={(changes) => save.mutate(changes)}
          />

          {save.isError ? (
            <p className="mt-2 text-[12px] text-[color:var(--color-state-blocked)]">
              {save.error.message}
            </p>
          ) : null}

          <Description
            key={shown.id}
            value={shown.description}
            disabled={save.isPending}
            onCommit={(value) => save.mutate({ description: value })}
          />

          <Subtasks item={shown} />

          {links.data === undefined ? null : (
            <Dependencies links={links.data} workItemId={item.id} />
          )}

          <TaskExtras workItemId={item.id} viewerId={session.data?.user.id ?? ''} />

          <section className="border-line mt-5 border-t pt-4">
            <div className="mb-3 flex items-center gap-0.5">
              {(['comments', 'activity'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setTab(option)}
                  aria-pressed={tab === option}
                  className={`rounded px-2.5 py-1 text-[12px] ${
                    tab === option
                      ? 'bg-surface-2 text-ink-900 font-medium'
                      : 'text-ink-500 hover:text-ink-900'
                  }`}
                >
                  {option === 'comments' ? t('view.comments') : t('activity.title')}
                </button>
              ))}
            </div>

            {tab === 'activity' ? (
              <ActivityFeed subjectId={item.id} project={key} />
            ) : comments.data === undefined ? (
              <p className="text-ink-500 font-mono text-[11px]">{t('common.loading')}</p>
            ) : comments.data.length === 0 ? (
              <p className="text-ink-500 text-[13px]">{t('view.noComments')}</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {comments.data.map((comment) => (
                  <CommentRow key={comment.id} comment={comment} workItemId={item.id} />
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="border-line flex items-center border-t px-5 py-2">
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('view.deleteTaskConfirm'))) remove.mutate()
            }}
            disabled={remove.isPending}
            className="text-ink-500 hover:text-[color:var(--color-state-blocked)] flex items-center gap-1.5 text-[11px] disabled:opacity-40"
          >
            <Icon name="trash" className="size-3" />
            {t('view.deleteTask')}
          </button>
          {remove.isError ? (
            <span className="ms-3 text-[11px] text-[color:var(--color-state-blocked)]">
              {remove.error.message}
            </span>
          ) : null}
        </div>

        {tab === 'comments' ? <Composer workItemId={item.id} /> : null}
      </aside>
    </>
  )
}

/**
 * Status, assignee, priority and due date — the four a person changes while
 * looking at the task.
 *
 * Each writes on change rather than behind a save button: there is no draft
 * here worth losing, and a form nobody submits is a field nobody updates.
 */
function Fields({
  item,
  projectKey,
  saving,
  onChange,
}: {
  item: WorkItem
  projectKey: string | undefined
  saving: boolean
  onChange: (changes: WorkItemChanges) => void
}) {
  const { t } = useTranslation()

  // The board is the authority on which columns this project has, and the
  // query key is the board tab's, so opening a panel does not refetch it.
  const board = useQuery<Board, ApiError>({
    queryKey: ['board', projectKey],
    queryFn: () => fetchBoard(projectKey as string),
    enabled: projectKey !== undefined,
    retry: false,
  })

  // A guest gets 404 from the directory by design, so this simply stays empty
  // for them and the assignee falls back to being read-only.
  const people = useQuery<Person[], ApiError>({
    queryKey: ['people'],
    queryFn: fetchPeople,
    retry: false,
  })

  const states = board.data?.columns ?? []

  return (
    <dl className="grid grid-cols-[110px_minmax(0,1fr)] items-center gap-y-2.5 text-[13px]">
      <dt className="text-ink-500">{t('work.status')}</dt>
      <dd>
        {states.length === 0 ? (
          <StatusPill name={item.state?.name ?? null} category={item.state?.category ?? null} compact />
        ) : (
          <Select
            value={item.state?.id ?? ''}
            disabled={saving}
            label={t('work.status')}
            onChange={(value) => onChange({ stateId: value === '' ? null : value })}
            options={[
              { value: '', label: '—' },
              ...states.map((state) => ({ value: state.id, label: state.name })),
            ]}
          />
        )}
      </dd>

      <dt className="text-ink-500">{t('work.assignee')}</dt>
      <dd className="flex items-center gap-2">
        <Avatar name={item.assignee?.name ?? null} color={item.assignee?.color} size="sm" />
        {people.data === undefined ? (
          <span className="text-ink-700">{item.assignee?.name ?? t('work.unassigned')}</span>
        ) : (
          <Select
            value={item.assignee?.id ?? ''}
            disabled={saving}
            label={t('work.assignee')}
            onChange={(value) => onChange({ assigneeId: value === '' ? null : value })}
            options={[
              { value: '', label: t('work.unassigned') },
              ...people.data.map((person) => ({ value: person.id, label: person.name })),
            ]}
          />
        )}
      </dd>

      <dt className="text-ink-500">{t('work.priority')}</dt>
      <dd className="flex items-center gap-1.5">
        <PriorityFlag priority={item.priority} />
        <Select
          value={item.priority}
          disabled={saving}
          label={t('work.priority')}
          onChange={(value) => onChange({ priority: value as Priority })}
          options={PRIORITIES.map((priority) => ({
            value: priority,
            label: t(`priority.${priority}`),
          }))}
        />
      </dd>

      <dt className="text-ink-500">{t('work.type')}</dt>
      <dd>
        <Select
          value={item.type}
          disabled={saving}
          label={t('work.type')}
          onChange={(value) => onChange({ type: value as WorkItemType })}
          options={TYPES.map((type) => ({ value: type, label: t(`work.type_${type}`) }))}
        />
      </dd>

      <dt className="text-ink-500">{t('work.startDate')}</dt>
      <dd>
        <DateField
          value={item.startDate}
          label={t('work.startDate')}
          disabled={saving}
          onChange={(value) => onChange({ startDate: value })}
        />
      </dd>

      <dt className="text-ink-500">{t('work.dueDate')}</dt>
      <dd>
        <DateField
          value={item.dueDate}
          label={t('work.dueDate')}
          disabled={saving}
          onChange={(value) => onChange({ dueDate: value })}
        />
      </dd>

      <dt className="text-ink-500">{t('work.estimate')}</dt>
      <dd className="flex items-center gap-1.5">
        <InlineText
          value={item.estimate === null ? '' : String(item.estimate)}
          label={t('work.estimate')}
          disabled={saving}
          placeholder="—"
          onCommit={(value) => {
            const trimmed = value.trim()
            if (trimmed === '') {
              if (item.estimate !== null) onChange({ estimate: null })

              return
            }

            // A number the server would reject is a number this field should
            // not send: leaving it as typed lets the person fix it in place.
            const parsed = Number(trimmed)
            if (Number.isFinite(parsed) && parsed >= 0 && parsed !== item.estimate) {
              onChange({ estimate: parsed })
            }
          }}
          className="text-ink-700 border-line bg-surface w-20 rounded border px-2 py-1 font-mono text-[12px]"
        />
        <span className="text-ink-500 text-[11px]">{t('work.estimateUnit')}</span>
      </dd>
    </dl>
  )
}

/**
 * A date input that speaks the API's language: an empty box clears the date
 * rather than sending an empty string the server would reject.
 */
function DateField({
  value,
  label,
  disabled,
  onChange,
}: {
  value: string | null
  label: string
  disabled: boolean
  onChange: (value: string | null) => void
}) {
  return (
    <input
      type="date"
      value={value?.slice(0, 10) ?? ''}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
      className="border-line bg-surface text-ink-700 rounded border px-2 py-1 font-mono text-[12px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
    />
  )
}

/**
 * Free text that saves when you leave it, not as you type.
 *
 * Every other field here writes on change, which is right for a dropdown and
 * wrong for prose: a request per keystroke, and an activity row for each. Blur
 * commits, Escape abandons, Enter is a shortcut for blur.
 */
function InlineText({
  value,
  label,
  disabled,
  placeholder,
  className,
  onCommit,
}: {
  value: string
  label: string
  disabled: boolean
  placeholder?: string
  className?: string
  onCommit: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  const [editing, setEditing] = useState(false)

  // While nobody is typing, the server's answer wins — an edit made elsewhere,
  // or the value coming back changed, should not be masked by a stale draft.
  if (!editing && draft !== value) setDraft(value)

  return (
    <input
      value={draft}
      disabled={disabled}
      aria-label={label}
      placeholder={placeholder}
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false)
        onCommit(draft.trim())
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') {
          setDraft(value)
          setEditing(false)
          event.currentTarget.blur()
        }
      }}
      className={`bg-transparent outline-none focus:bg-[color:var(--color-surface-2)] disabled:opacity-50 ${className ?? ''}`}
    />
  )
}

/**
 * The description, which until now could be written by the API and read by a
 * person but never edited by one.
 */
function Description({
  value,
  disabled,
  onCommit,
}: {
  value: string | null
  disabled: boolean
  onCommit: (value: string | null) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(value ?? '')

  return (
    <section className="border-line mt-4 border-t pt-4">
      <h3 className="text-ink-500 mb-1.5 font-mono text-[10px] tracking-widest uppercase">
        {t('view.description')}
      </h3>
      <textarea
        value={draft}
        rows={Math.min(14, Math.max(3, draft.split('\n').length + 1))}
        disabled={disabled}
        aria-label={t('view.description')}
        placeholder={t('view.descriptionPlaceholder')}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const next = draft.trim() === '' ? null : draft
          if (next !== value) onCommit(next)
        }}
        className="border-line bg-surface text-ink-700 w-full resize-y rounded border px-2.5 py-2 text-sm outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
      />
    </section>
  )
}

/**
 * Children, one level deep.
 *
 * Breaking a task down is the move this panel was missing: `parent_id` has been
 * in the schema and the API since Phase 1 with nothing in front of it.
 */
function Subtasks({ item }: { item: WorkItem }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')

  const add = useMutation<WorkItem, ApiError, string>({
    mutationFn: (name) =>
      createWorkItem({ projectId: item.projectId, title: name, parentId: item.id }),
    onSuccess: () => {
      setTitle('')
      void queryClient.invalidateQueries({ queryKey: ['work-item', item.id] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      void queryClient.invalidateQueries({ queryKey: ['board'] })
    },
  })

  const children = item.subtasks ?? []
  const done = children.filter((child) => child.closedAt !== null).length

  return (
    <section className="border-line mt-4 border-t pt-4">
      <h3 className="text-ink-500 mb-2 flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase">
        {t('view.subtasks')}
        {children.length === 0 ? null : (
          <span className="text-ink-700">
            {done}/{children.length}
          </span>
        )}
      </h3>

      {children.length === 0 ? null : (
        <ul className="mb-2 flex flex-col gap-1">
          {children.map((child: WorkItemChild) => (
            <li key={child.id} className="flex items-center gap-2 text-[13px]">
              <span className="text-ink-500 shrink-0 font-mono text-[10px]">{child.key}</span>
              <span
                className={`min-w-0 flex-1 truncate ${
                  child.closedAt === null ? 'text-ink-900' : 'text-ink-500 line-through'
                }`}
              >
                {child.title}
              </span>
              <StatusPill
                name={child.state?.name ?? null}
                category={child.state?.category ?? null}
                compact
              />
              <Avatar name={child.assignee?.name ?? null} color={child.assignee?.color} size="sm" />
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault()
          const name = title.trim()
          if (name !== '') add.mutate(name)
        }}
      >
        <input
          value={title}
          disabled={add.isPending}
          aria-label={t('view.addSubtask')}
          placeholder={t('view.addSubtask')}
          onChange={(event) => setTitle(event.target.value)}
          className="border-line bg-surface text-ink-700 w-full rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
        />
      </form>

      {add.isError ? (
        <p className="mt-1 text-[12px] text-[color:var(--color-state-blocked)]">
          {add.error.message}
        </p>
      ) : null}
    </section>
  )
}

function Select({
  value,
  options,
  label,
  disabled,
  onChange,
}: {
  value: string
  options: { value: string; label: string }[]
  label: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      className="border-line bg-surface text-ink-700 min-w-0 max-w-full rounded border px-2 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

/**
 * One comment, with the two things that were missing: correcting it and
 * removing it.
 *
 * The server holds both to author-or-manager, so this shows the controls to
 * everyone and lets a refusal come back as an error rather than trying to
 * predict the policy in the client and getting it subtly wrong.
 */
function CommentRow({ comment, workItemId }: { comment: Comment; workItemId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [body, setBody] = useState(comment.body)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['comments', workItemId] })
    void queryClient.invalidateQueries({ queryKey: ['activities'] })
  }

  const save = useMutation<Comment, ApiError, void>({
    mutationFn: () => updateComment(comment.id, { body: body.trim() }),
    onSuccess: () => {
      setEditing(false)
      refresh()
    },
  })

  const remove = useMutation<void, ApiError, void>({
    mutationFn: () => deleteComment(comment.id),
    onSuccess: refresh,
  })

  return (
    <li className="group flex gap-2.5">
      <Avatar name={comment.author?.name ?? null} color={comment.author?.color} size="sm" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-ink-900 text-[12px] font-medium">
            {comment.author?.name ?? '—'}
          </span>
          <span className="text-ink-500 font-mono text-[10px]">
            {comment.createdAt?.slice(0, 10) ?? ''}
          </span>
          <VisibilityBadge visibility={comment.visibility} />
          {comment.edited ? (
            <span className="text-ink-500 font-mono text-[9px] tracking-wide uppercase">
              {t('view.edited')}
            </span>
          ) : null}

          <span className="ms-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              type="button"
              onClick={() => setEditing(true)}
              title={t('view.edit')}
              aria-label={t('view.edit')}
              className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 rounded p-1"
            >
              <Icon name="pencil" className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('view.deleteComment'))) remove.mutate()
              }}
              title={t('view.delete')}
              aria-label={t('view.delete')}
              className="text-ink-500 hover:bg-surface-2 rounded p-1 hover:text-[color:var(--color-state-blocked)]"
            >
              <Icon name="trash" className="size-3" />
            </button>
          </span>
        </div>

        {editing ? (
          <form
            className="mt-1 flex flex-col gap-1.5"
            onSubmit={(event) => {
              event.preventDefault()
              if (body.trim() !== '') save.mutate()
            }}
          >
            <textarea
              value={body}
              rows={2}
              autoFocus
              onChange={(event) => setBody(event.target.value)}
              className="border-line bg-surface w-full resize-none rounded border px-2 py-1 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
            />
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={save.isPending}
                className="bg-brand-600 rounded px-2.5 py-1 text-[11px] text-white disabled:opacity-40"
              >
                {t('view.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setBody(comment.body)
                  setEditing(false)
                }}
                className="text-ink-500 px-1.5 py-1 text-[11px]"
              >
                {t('view.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <p className="text-ink-700 mt-0.5 text-[13px] whitespace-pre-line">{comment.body}</p>
        )}

        {save.isError || remove.isError ? (
          <p className="mt-1 text-[11px] text-[color:var(--color-state-blocked)]">
            {(save.error ?? remove.error)?.message}
          </p>
        ) : null}
      </div>
    </li>
  )
}

/**
 * What this item waits on, and what waits on it.
 *
 * Both directions were readable here before and neither was writable, so a
 * dependency could only be created by something that was not a person.
 */
function Dependencies({ links, workItemId }: { links: ItemLinks; workItemId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [key, setKey] = useState('')
  const [direction, setDirection] = useState<'blocks' | 'blockedBy'>('blockedBy')

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['links', workItemId] })
    void queryClient.invalidateQueries({ queryKey: ['timeline'] })
  }

  const add = useMutation<void, ApiError, string>({
    mutationFn: (identifier) => linkWorkItems(workItemId, { workItemId: identifier, direction }),
    onSuccess: () => {
      setKey('')
      refresh()
    },
  })

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (linkId) => unlinkWorkItems(workItemId, linkId),
    onSuccess: refresh,
  })

  return (
    <section className="border-line mt-5 border-t pt-4">
      <h3 className="text-ink-500 mb-2 font-mono text-[10px] tracking-widest uppercase">
        {t('view.dependencies')}
      </h3>

      {[
        { label: t('view.blockedBy'), rows: links.blockedBy },
        { label: t('view.blocks'), rows: links.blocks },
      ]
        .filter((group) => group.rows.length > 0)
        .map((group) => (
          <div key={group.label} className="mb-3">
            <h4 className="text-ink-500 mb-1.5 text-[11px]">{group.label}</h4>
            <ul className="flex flex-col gap-1">
              {group.rows.map((row) => (
                <li key={row.linkId} className="flex items-center gap-2 text-[13px]">
                  <Icon name="link" className="text-ink-500 size-3.5 shrink-0" />
                  <span className="text-brand-600 font-mono text-[11px]">{row.key}</span>
                  <span className="text-ink-700 min-w-0 flex-1 truncate">{row.title}</span>
                  <button
                    type="button"
                    onClick={() => remove.mutate(row.linkId)}
                    disabled={remove.isPending}
                    title={t('view.removeLink')}
                    aria-label={t('view.removeLink')}
                    className="text-ink-500 hover:text-[color:var(--color-state-blocked)] shrink-0 disabled:opacity-40"
                  >
                    <Icon name="close" className="size-3" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}

      <form
        className="flex items-center gap-1.5"
        onSubmit={(event) => {
          event.preventDefault()
          const identifier = key.trim()
          if (identifier !== '') add.mutate(identifier)
        }}
      >
        <select
          value={direction}
          aria-label={t('view.linkDirection')}
          onChange={(event) => setDirection(event.target.value as 'blocks' | 'blockedBy')}
          className="border-line bg-surface text-ink-700 rounded border px-1.5 py-1 text-[12px] outline-none focus:border-[color:var(--color-brand-300)]"
        >
          <option value="blockedBy">{t('view.blockedBy')}</option>
          <option value="blocks">{t('view.blocks')}</option>
        </select>
        <input
          value={key}
          disabled={add.isPending}
          aria-label={t('view.addLink')}
          placeholder={t('view.addLinkPlaceholder')}
          onChange={(event) => setKey(event.target.value)}
          className="border-line bg-surface text-ink-700 min-w-0 flex-1 rounded border px-2 py-1 font-mono text-[12px] outline-none focus:border-[color:var(--color-brand-300)] disabled:opacity-50"
        />
      </form>

      {add.isError ? (
        <p className="mt-1 text-[12px] text-[color:var(--color-state-blocked)]">
          {add.error.message}
        </p>
      ) : null}
    </section>
  )
}

function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  const { t } = useTranslation()

  return (
    <span
      className={`rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase ${
        visibility === 'client' ? 'bg-brand-50 text-brand-700' : 'bg-surface-2 text-ink-500'
      }`}
    >
      {visibility === 'client' ? t('view.clientVisible') : t('view.internal')}
    </span>
  )
}

function Composer({ workItemId }: { workItemId: string }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<Visibility>('internal')

  const send = useMutation<Comment, ApiError, void>({
    mutationFn: () => postComment(workItemId, { body: body.trim(), visibility }),
    onSuccess: () => {
      setBody('')
      void queryClient.invalidateQueries({ queryKey: ['comments', workItemId] })
      void queryClient.invalidateQueries({ queryKey: ['activities'] })
    },
  })

  return (
    <form
      className="border-line bg-surface-2 border-t px-5 py-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (body.trim().length > 0) send.mutate()
      }}
    >
      <div className="mb-2 flex items-center gap-1">
        {(['internal', 'client'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setVisibility(option)}
            aria-pressed={visibility === option}
            className={`rounded px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase ${
              visibility === option
                ? option === 'client'
                  ? 'bg-brand-600 text-white'
                  : 'bg-ink-900 text-white'
                : 'text-ink-500 hover:bg-surface'
            }`}
          >
            {option === 'client' ? t('view.clientVisible') : t('view.internal')}
          </button>
        ))}
      </div>

      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        placeholder={t('view.addComment')}
        rows={2}
        className="border-line bg-surface w-full resize-none rounded border px-2.5 py-1.5 text-[13px] outline-none focus:border-[color:var(--color-brand-300)]"
      />

      <div className="mt-1.5 flex items-center gap-3">
        <p className="text-ink-500 min-w-0 flex-1 text-[10px] leading-tight">
          {t('view.visibilityNote')}
        </p>
        <button
          type="submit"
          disabled={send.isPending || body.trim().length === 0}
          className="bg-brand-600 shrink-0 rounded px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-40"
        >
          {t('view.send')}
        </button>
      </div>
    </form>
  )
}
